import React from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import HeadphonesIcon from "@mui/icons-material/Headphones";

// Dialog shown when multiple files are uploaded, asking user how to handle them.
export interface CombineFilesDialogProps {
    isOpen: boolean;
    onClose: () => void;
    files: Array<{ file: File }>;
    onKeepSeparate: () => void;
    onCombineIntoSequence: () => void;
}

export const CombineFilesDialog: React.FC<CombineFilesDialogProps> = ({
    isOpen,
    onClose,
    files,
    onKeepSeparate,
    onCombineIntoSequence,
}) => {
    const truncateFilename = (name: string, maxLen: number = 28) => {
        if (name.length <= maxLen) return name;
        const ext = name.lastIndexOf(".") > 0 ? name.slice(name.lastIndexOf(".")) : "";
        const baseName = name.slice(0, name.lastIndexOf(".") > 0 ? name.lastIndexOf(".") : name.length);
        const truncated = baseName.slice(0, maxLen - ext.length - 3) + "...";
        return truncated + ext;
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: "460px",
                    padding: "20px 24px",
                    borderRadius: "12px",
                    boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.08)",
                },
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                <Typography
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "15px",
                        fontWeight: 500,
                        lineHeight: "22px",
                        color: "#1A1A1A",
                    }}
                >
                    Are these the same recording?
                </Typography>
                <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{
                        color: "#9CA3AF",
                        padding: "4px",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                    }}
                >
                    <Box component="span" sx={{ fontSize: "18px", lineHeight: 1 }}>
                        ×
                    </Box>
                </IconButton>
            </Box>

            <Typography
                sx={{
                    fontFamily: "Inter",
                    fontSize: "13px",
                    fontWeight: 400,
                    color: "#6B7280",
                    mb: 2.5,
                    lineHeight: "18px",
                }}
            >
                Combine your files into a sequence when they are from different mics, cameras, or people in a recording.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 3 }}>
                {files.map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                            <HeadphonesIcon sx={{ fontSize: 18, color: "#9CA3AF", flexShrink: 0 }} />
                            <Typography
                                sx={{
                                    fontFamily: "Inter",
                                    fontSize: "13px",
                                    fontWeight: 400,
                                    color: "#1A1A1A",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {truncateFilename(item.file.name)}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1.5 }}>
                <Box
                    component="button"
                    onClick={onKeepSeparate}
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#1A1A1A",
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        padding: "8px 16px",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        "&:hover": {
                            backgroundColor: "#F9FAFB",
                            borderColor: "#D1D5DB",
                        },
                    }}
                >
                    Keep separate
                </Box>
                <Box
                    component="button"
                    onClick={onCombineIntoSequence}
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#FFFFFF",
                        backgroundColor: "#1A1A1A",
                        border: "1px solid #1A1A1A",
                        borderRadius: "8px",
                        padding: "8px 16px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        transition: "all 0.15s ease",
                        "&:hover": {
                            backgroundColor: "#333333",
                        },
                    }}
                >
                    <Box component="span" sx={{ fontSize: "14px" }}>
                        ≡
                    </Box>
                    Combine into sequence
                </Box>
            </Box>
        </Dialog>
    );
};

